import { ExtensionRegistry } from '@repo/mesh-contract';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  HostDirectoryService,
  BroadcastService,
  HostInviteService,
  ControlPlaneJournal,
  ScopedRbacEvaluator,
  deriveSettingsAccess,
  type VerifiedPrincipal,
  type SettingsAccess,
} from 'mesh-microkernel';
import { Header } from './Header';
import { PrincipalProvider } from './identity/principal-context.js';
import { KnockingNotificationToast, type KnockItem } from './KnockingNotificationToast';
import { DockedRoomView } from './channels/DockedRoomView';

import { ReviewEnrollmentContractDrawer } from './components/ReviewEnrollmentContractDrawer';
import { Sidebar } from './Sidebar';
import { ModuleLoader } from './modules/ModuleLoader';
import { AgentDirectoryPane } from './AgentDirectoryPane';
import { crmPlugin } from '../plugins/crm/crm-plugin';
import { SEED_TASK_DETAILS } from '@repo/mesh-contract';
import { getDemoKanbanTasks } from '../fixtures/kanban-demo-fixtures';
import { globalWorkspaceDirectory } from '../mesh/governance/workspace-directory';
import { globalWorkspaceBootstrapper } from '../workspace/workspace-bootstrap';
import { WorkspaceWizard } from './onboarding/WorkspaceWizard';
import { PluginDegradedBanner } from './PluginDegradedBanner';
import { isChannelEnabled, isPluginIdEnabled } from './channels/channel-plugin-registry.js';
import {
  ChannelBindingRegistry,
  TriggerMatcher,
  InMemoryKanbanProvider,
  ScrumMasterWorkflow,
  ChannelAgentRouter,
  ProposalService,
  ProposalExecutor,
  AuditLogger,
  type KanbanProposal,
  type ExecuteProposalRequest,
  type StandupResponse,
  type BindingId,
  type ChannelId,
  type AgentId,
  type TenantId,
  type EntityId,
} from 'mesh-microkernel';
import { AppContainer } from './AppContainer';
import { DirectoryDrawer } from './DirectoryDrawer';
import { InvitePopover } from './InvitePopover';
import { NodeMetricsFooter } from './NodeMetricsFooter';
import { OnboardingModal, UserProfileData } from './OnboardingModal';
import { UserSettingsModal } from './UserSettingsModal';
import { DePINMarketplaceModal } from './components/DePINMarketplaceModal';
import { MobileDeviceSimulator } from './components/MobileDeviceSimulator';
import { UserProfileDrawer } from './identity/UserProfileDrawer.js';
import { AvatarPhotoEditor } from './identity/AvatarPhotoEditor.js';
import type { MeshUserProfileReadModel } from '../identity/projections/user-profile-projection';
import type { ExecutionContext } from '../identity/execution-context';
import { StepUpMfaModal } from './StepUpMfaModal';
import { AuthLandingGateway } from './auth/components/AuthLandingGateway';
import { SecretKeyRegistrationGateway } from './auth/components/SecretKeyRegistrationGateway';
import { DecryptRevealTransition } from './auth/components/DecryptRevealTransition';
import { IdleLockScreen } from './auth/components/IdleLockScreen';
import { multiAccountVault } from './auth/multi-account-vault';
import { sessionIntrospector } from './auth/services/SessionIntrospector';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { SovereignOrb } from './agents/components/SovereignOrb';
import { KnowledgeQueryWidget } from './agents/components/KnowledgeQueryWidget';
import { GenerativeDashboardCanvas } from './agents/components/GenerativeDashboardCanvas';
import { VoiceWaveformOverlay } from './agents/components/VoiceWaveformOverlay';
import { SystemArchitectReviewModal } from './agents/components/SystemArchitectReviewModal';
import { UserManagementConsole } from './admin/components/UserManagementConsole';
import { LanguageManagerModal } from './i18n/components/LanguageManagerModal';
import { GStackAgentsHubModal } from './agents/components/GStackAgentsHubModal';
import { SovereignModuleSlotRenderer } from './modules/SovereignModuleSlotRenderer.js';
import { sovereignModuleSlotManager } from './modules/SovereignModuleSlotManager.js';
import { NotificationPopover, NotificationItem } from './NotificationPopover';
import { ResizeHandle } from './ResizeHandle';
import { ThemePreset, getInitialTheme, applyThemeTokens } from '../theme/themeEngine';
import { useBreakpoint } from './useBreakpoint';
import { GenericChannelView } from '@herx-module/chat';



import { globalCalendarBridge } from '../plugins/action-planner/calendar/calendar-work-item-bridge';
import { globalWorkItemStore } from '../plugins/kanban/work-item-store';
import { getKanbanTasks, updateKanbanTaskStatus } from '../plugins/kanban';

import { TaskFlowWorkspace } from './taskflow/TaskFlowWorkspace.js';
import { KioskShell } from './kiosk/KioskShell';
import { KioskCatalogView } from './kiosk/KioskCatalogView';
import { KioskSelfCheckoutModal } from './kiosk/KioskSelfCheckoutModal';
import { AccountSwitcherModal } from '../plugins/identity/AccountSwitcherModal.js';
import { globalIdentityService } from '../plugins/identity/identityService.js';
import type { AuthorizationContext } from '../kernel/authorization/capabilities.js';
import { InventoryManagerView } from './commerce/InventoryManagerView.js';
import { CmsStudioView } from './cms/CmsStudioView.js';
import { globalCatalogService } from '../plugins/commerce/catalog-service.js';
import { globalInventoryMovementLedger } from '../plugins/commerce/inventory/inventoryMovementLedger.js';
import { globalMediaAssetService } from '../plugins/commerce/media/mediaAssetService.js';
import type {
  GuestCart,
  GuestCartItem,
  PublicProductProjection,
  PublicVariantProjection,
} from '../plugins/storefront/storefront-contracts';
import { ScreenReaderLiveAnnouncer } from '../a11y/ScreenReaderLiveAnnouncer.js';
import { CommandPaletteModal } from './components/CommandPaletteModal.js';
import { ContextualHelpDrawer } from './components/ContextualHelpDrawer.js';
import { KnowledgePrimingDashboard } from './admin/KnowledgePrimingDashboard.js';
import { CmsTranslationStudio } from './cms/CmsTranslationStudio.js';
import { ChannelManagementModal } from './channels/ChannelManagementModal.js';

// Define default enabled capabilities for lean messenger
export const DEFAULT_ENABLED_MODULES: Record<string, boolean> = {
  "mod-chat": true,
  "mod-kanban": false,
  "mod-wall": false,
  "mod-whiteboard": false,
  "mod-scrum": false,
  "mod-crm": false,
  "mod-calculator": false,
  "mod-clock": false,
  "mod-door-release": false,
  "mod-gallery": false,
  "mod-trader": false,
};

import { isModuleEnabled } from './modules/ModuleRegistry.js';

export function getActiveModules(): Record<string, boolean> {
  const active: Record<string, boolean> = { ...DEFAULT_ENABLED_MODULES };
  if (typeof window !== 'undefined') {
    for (const key of Object.keys(active)) {
      active[key] = isModuleEnabled(key);
    }
  }
  return active;
}
import { EfrisItemMappingDesk } from './invoicing/EfrisItemMappingDesk.js';
import { ComplianceAuditDeskView } from './invoicing/ComplianceAuditDeskView.js';
import { PayrollManagementDesk } from './finance/PayrollManagementDesk.js';
import { GalleryChannelView } from './gallery/GalleryChannelView.js';
import { ActivationModal } from './licensing/ActivationModal.js';
import { PortalLandingView } from './landing/PortalLandingView.js';
import { SuperadminDashboard } from './admin/SuperadminDashboard.js';
import { ExtensionManagerPane } from './settings/ExtensionManagerPane.js';

function getLivePublicProducts(): readonly PublicProductProjection[] {
  const allProds = globalCatalogService.getAllProducts();
  const allVars = globalCatalogService.getAllVariants();
  const projections = globalInventoryMovementLedger.getAllStockProjections();

  return allProds
    .filter((p) => p.status === 'active')
    .map((prod) => {
      const vars = allVars.filter((v) => v.productId === prod.productId);
      const publicVariants: PublicVariantProjection[] = vars.map((v) => {
        const stockProj = projections.find((sp) => sp.sku === v.sku);
        const available = stockProj?.availableToSell ?? 10;
        const stockStatus = available <= 0 ? 'out_of_stock' as const : available <= 5 ? 'low_stock' as const : 'in_stock' as const;
        const priceMinor = parseInt(v.basePriceMinor || '0', 10);

        return {
          variantId: v.variantId,
          sku: v.sku,
          name: v.attributes ? Object.values(v.attributes).join(' / ') || v.sku : v.sku,
          basePriceMinor: priceMinor,
          currency: v.currency || 'USD',
          stockStatus,
          attributes: v.attributes,
        };
      });

      const title = typeof prod.title === 'string' ? prod.title : prod.title?.['en-US'] || Object.values(prod.title || {})[0] || 'Product';
      const description = typeof prod.description === 'string' ? prod.description : prod.description ? prod.description['en-US'] || Object.values(prod.description)[0] : undefined;

      const minPriceMinor = publicVariants.length > 0
        ? Math.min(...publicVariants.map((v) => v.basePriceMinor))
        : 0;

      const hasInStock = publicVariants.some((v) => v.stockStatus === 'in_stock');
      const hasLowStock = publicVariants.some((v) => v.stockStatus === 'low_stock');
      const overallStockStatus = hasInStock ? 'in_stock' as const : hasLowStock ? 'low_stock' as const : 'out_of_stock' as const;

      const mediaList = globalMediaAssetService.getMediaForOwner('product', prod.productId);
      const primaryMediaAsset = mediaList.find((m) => m.role === 'primary') || mediaList[0];
      const primaryMediaRef = prod.media?.[0];
      const directImage = primaryMediaAsset?.mediaId || primaryMediaRef?.attachmentId;

      // High-resolution fallback imagery based on title or category
      const fallbackImages: Record<string, string> = {
        beverages: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=600&auto=format&fit=crop&q=80',
        coffee: 'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=600&auto=format&fit=crop&q=80',
        espresso: 'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=600&auto=format&fit=crop&q=80',
        hardware: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&auto=format&fit=crop&q=80',
        security: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&auto=format&fit=crop&q=80',
        camera: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&auto=format&fit=crop&q=80',
        bakery: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=600&auto=format&fit=crop&q=80',
        food: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&auto=format&fit=crop&q=80',
      };

      const titleLower = title.toLowerCase();
      const matchedKey = Object.keys(fallbackImages).find((k) => titleLower.includes(k) || (prod.categoryIds?.[0] || '').toLowerCase().includes(k));
      const imageUrl = (directImage && (directImage.startsWith('http') || directImage.startsWith('data:') || directImage.startsWith('./')))
        ? directImage
        : (matchedKey ? fallbackImages[matchedKey] : 'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=600&auto=format&fit=crop&q=80');

      const allergenInfo = (prod.schemaExtension as any)?.allergens;

      return {
        productId: prod.productId,
        name: title,
        slug: prod.productId,
        description,
        category: prod.categoryIds?.[0] || 'General',
        minPriceMinor,
        currency: vars[0]?.currency || 'USD',
        overallStockStatus,
        variants: publicVariants,
        imageUrl,
        allergenInfo,
        tags: prod.categoryIds,
      };
    });
}

const SAMPLE_POS_CATEGORIES = [
  { id: 'all', name: 'All Items' },
  { id: 'beverages', name: '☕ Beverages' },
  { id: 'bakery', name: '🥐 Bakery' },
  { id: 'food', name: '🥪 Food' },
];

const SAMPLE_POS_ITEMS = [
  { productId: 'p1', variantId: 'v_coffee_12', name: 'Pour-Over Coffee (12oz)', sku: 'COF-12OZ', categoryId: 'beverages', priceMinor: 450, stockStatus: 'in-stock' as const, imageUrl: 'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=400&auto=format&fit=crop&q=80' },
  { productId: 'p2', variantId: 'v_coffee_16', name: 'Pour-Over Coffee (16oz)', sku: 'COF-16OZ', categoryId: 'beverages', priceMinor: 550, stockStatus: 'in-stock' as const, imageUrl: 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=400&auto=format&fit=crop&q=80' },
  { productId: 'p3', variantId: 'v_matcha_reg', name: 'Oat Matcha Latte', sku: 'MATCHA-OAT', categoryId: 'beverages', priceMinor: 550, stockStatus: 'in-stock' as const, imageUrl: 'https://images.unsplash.com/photo-1536256263959-770b48d82b0a?w=400&auto=format&fit=crop&q=80' },
  { productId: 'p4', variantId: 'v_croiss_1', name: 'Butter Croissant', sku: 'BAKE-CROISS', categoryId: 'bakery', priceMinor: 375, stockStatus: 'low-stock' as const, imageUrl: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400&auto=format&fit=crop&q=80' },
  { productId: 'p5', variantId: 'v_toast_1', name: 'Avocado Sourdough Toast', sku: 'FOOD-AVO', categoryId: 'food', priceMinor: 850, stockStatus: 'in-stock' as const, imageUrl: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&auto=format&fit=crop&q=80' },
];

interface AppShellProps {
  nodeId: string;
  directory: HostDirectoryService;
  broadcast: BroadcastService;
  invite: HostInviteService;
}

export const AppShell: React.FC<AppShellProps> = ({ nodeId, directory, broadcast, invite }) => {
  const [knocks, setKnocks] = useState<KnockItem[]>([]);

  useEffect(() => {
    const fetchKnocks = async () => {
      try {
        const res = await fetch("/api/v1/invites/knocks/pending");
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.knocks)) {
          setKnocks(data.knocks);
          // Dispatch custom event for Sidebar status indicators
          window.dispatchEvent(new CustomEvent("knocks:updated", { detail: data.knocks }));
        }
      } catch {}
    };

    fetchKnocks();
    const interval = setInterval(fetchKnocks, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleAdmit = async (knockId: string, roomId: string) => {
    try {
      await fetch("/api/v1/invites/knocks/resolve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ knockId, action: "admit" })
      });
      setKnocks((prev) => prev.filter((k) => k.knockId !== knockId));
    } catch {}
  };

  const handleDeny = async (knockId: string, roomId: string, reason?: string) => {
    try {
      await fetch("/api/v1/invites/knocks/resolve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ knockId, action: "deny", rejectionReason: reason })
      });
      setKnocks((prev) => prev.filter((k) => k.knockId !== knockId));
    } catch {}
  };

  const breakpoint = useBreakpoint();
  const { isMobile, bp } = breakpoint;

  const shellMode = useMemo<'portal' | 'workspace' | 'admin'>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const modeParam = params.get('mode');
      if (modeParam === 'portal' || modeParam === 'workspace' || modeParam === 'admin') {
        return modeParam;
      }
      if (window.location.hostname === 'herx.org' || window.location.hostname === 'www.herx.org') {
        return 'portal';
      }
    }
    return 'workspace';
  }, []);

  const [currentRoom, setCurrentRoom] = useState(shellMode === 'portal' ? 'portal' : 'general');
  // On mobile sidebar starts closed; on desktop it starts open
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile && shellMode !== 'portal');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [topPaneHeight, setTopPaneHeight] = useState(280);
  const [isPaneExpanded, setIsPaneExpanded] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Escape key handler and global keyboard shortcuts (Cmd+K, Cmd+Shift+P, ?)
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isContextualHelpOpen, setIsContextualHelpOpen] = useState(false);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [isDePINMarketOpen, setIsDePINMarketOpen] = useState(false);
  const [isFleetHealthOpen, setIsFleetHealthOpen] = useState(false);
  const [isSuperadminDashboardOpen, setIsSuperadminDashboardOpen] = useState(false);
  const [isDevSimulatorOpen, setIsDevSimulatorOpen] = useState(false);

  // Hydrate Sovereign Module Platform Catalog & Slots
  useEffect(() => {
    const hydrateModules = async () => {
      try {
        const res = await fetch('/api/v1/admin/modules/available', {
          headers: { 'x-mesh-role': 'mesh_owner' },
        });
        if (res.ok) {
          const json = await res.json();
          if (json.ok && Array.isArray(json.data?.modules)) {
            sovereignModuleSlotManager.updateActiveModules(json.data.modules);
          }
        }
      } catch (err: any) {
        console.warn('[AppShell] Module slot hydration error:', err?.message);
      }
    };
    hydrateModules();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCalendarOpen) setIsCalendarOpen(false);
        if (isCommandPaletteOpen) setIsCommandPaletteOpen(false);
        if (isContextualHelpOpen) setIsContextualHelpOpen(false);
        if (isDePINMarketOpen) setIsDePINMarketOpen(false);
        if (isSuperadminDashboardOpen) setIsSuperadminDashboardOpen(false);
      }

      // Cmd+K or Ctrl+K for Command Palette
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      }

      // Cmd+Shift+P or Ctrl+Shift+P for Sovereign DePIN Marketplace
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setIsDePINMarketOpen((prev) => !prev);
      }

      // Ctrl+Shift+M — Mobile Device Simulator (dev/staging only)
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        setIsDevSimulatorOpen((prev) => !prev);
      }

      // ? for Help Drawer (only when not focused on an input/textarea)
      if (
        e.key === '?' &&
        !['INPUT', 'TEXTAREA', 'SELECT'].includes((document.activeElement?.tagName || ''))
      ) {
        e.preventDefault();
        setIsContextualHelpOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCalendarOpen, isCommandPaletteOpen, isContextualHelpOpen, isDePINMarketOpen]);

  const [authContext, setAuthContext] = useState<AuthorizationContext>(() => globalIdentityService.getAuthorizationContext());
  const [isAccountSwitcherOpen, setIsAccountSwitcherOpen] = useState(false);
  const [vaultUser, setVaultUser] = useState<any>(() => multiAccountVault.getCurrentUser());

  useEffect(() => {
    const updateVault = () => setVaultUser(multiAccountVault.getCurrentUser());
    multiAccountVault.on('auth:login', updateVault);
    multiAccountVault.on('auth:logout', updateVault);
    multiAccountVault.on('auth:stateChanged', updateVault);
    multiAccountVault.on('account:switched', updateVault);
    return () => {
      multiAccountVault.off('auth:login', updateVault);
      multiAccountVault.off('auth:logout', updateVault);
      multiAccountVault.off('auth:stateChanged', updateVault);
      multiAccountVault.off('account:switched', updateVault);
    };
  }, []);

  useEffect(() => {
    return globalIdentityService.subscribe((ctx) => {
      setAuthContext(ctx);
    });
  }, []);

  const isElectron = typeof window !== 'undefined' && Boolean((window as any).electronAPI);

  const isOwner = useMemo(() => {
    const current = vaultUser || multiAccountVault.getCurrentUser();
    const vaultRole = current?.role as string | undefined;
    const isVaultAdmin =
      vaultRole === 'admin' ||
      vaultRole === 'owner' ||
      vaultRole === 'mesh_owner' ||
      vaultRole === 'superadmin';
    const isGenesisOwner =
      typeof window !== 'undefined' &&
      (localStorage.getItem('sovereign_genesis_completed') === 'true' ||
        localStorage.getItem('sovereign_genesis_configured') === 'true' ||
        localStorage.getItem('sovereign_owner_authenticated') === 'true');
    const authRoles = (authContext.roles as string[]) || [];
    const isAuthContextOwner =
      authRoles.includes('sovereign-owner') ||
      authRoles.includes('admin') ||
      authRoles.includes('owner') ||
      authRoles.includes('mesh_owner');

    return (
      Boolean(isVaultAdmin) ||
      isGenesisOwner ||
      isAuthContextOwner ||
      (isElectron && authContext.sessionMode.type === 'authenticated')
    );
  }, [vaultUser, authContext, isElectron]);

  const verifiedPrincipal = useMemo<VerifiedPrincipal>(() => {
    return {
      principalId: authContext.principal.id,
      meshId: 'mesh-sovereign-alpha',
      keyId: `key-${authContext.principal.id}`,
      authenticated: authContext.principal.authenticated,
      roles: isOwner ? ['owner'] : ['member'],
    };
  }, [authContext.principal, isOwner]);

  const settingsAccess = useMemo<SettingsAccess>(() => {
    const keyResolver = {
      resolvePublicKey: (actorId: string) => new TextEncoder().encode(`pubkey-${actorId}`),
      isKeyRevoked: () => false,
    };
    const verifier = {
      verifySignature: () => true,
    };
    const journal = new ControlPlaneJournal({ signatureVerifier: verifier, keyResolver, meshId: 'mesh-sovereign-alpha' });
    
    journal.appendEnvelope(
      {
        payload: {
          domain: 'mesh-control-plane/v1',
          meshId: 'mesh-sovereign-alpha',
          actorId: 'sovereign-genesis',
          keyId: 'key-1',
          nonce: `nonce-init-${nodeId}`,
          createdAt: Date.now(),
          expiresAt: Date.now() + 60_000,
          expectedPolicyVersion: journal.getPolicyVersion(),
          action: 'ASSIGN_ROLE',
          targetSubjectId: nodeId,
          scope: { kind: 'system' },
          role: isOwner ? 'owner' : 'member',
        },
        signature: 'valid-sig',
      },
      Date.now()
    );

    const evaluator = new ScopedRbacEvaluator({ journal, keyResolver, meshId: 'mesh-sovereign-alpha' });
    return deriveSettingsAccess(evaluator, verifiedPrincipal, Date.now());
  }, [nodeId, isOwner, verifiedPrincipal]);

  const [isOnboardingOpen, setIsOnboardingOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (isOwner) {
      return !localStorage.getItem('sovereign_genesis_completed') && !localStorage.getItem('sovereign_genesis_configured');
    }
    // Ordinary users should NEVER be blocked by genesis/onboarding modal or DAG/LLM configuration
    return false;
  });

  const [isAuthGatewayOpen, setIsAuthGatewayOpen] = useState(false);
  const [isFirstLaunch, setIsFirstLaunch] = useState(
    () => multiAccountVault.getActiveAccount() === null
  );
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [isKnowledgeWidgetOpen, setIsKnowledgeWidgetOpen] = useState(false);
  const [isGenUICanvasOpen, setIsGenUICanvasOpen] = useState(false);
  const [isVoiceOverlayOpen, setIsVoiceOverlayOpen] = useState(false);
  const [isArchitectModalOpen, setIsArchitectModalOpen] = useState(false);
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isLanguageManagerOpen, setIsLanguageManagerOpen] = useState(false);
  const [isAgentsHubOpen, setIsAgentsHubOpen] = useState(false);

  // Optimistic non-blocking instant logout
  const handleImmediateLogout = () => {
    setIsDecrypting(true);
    multiAccountVault.signOut();

    Promise.allSettled([
      sessionIntrospector.stop(),
    ]).catch((err) => console.warn('[Logout] Background teardown notice:', err));

    setTimeout(() => {
      setIsDecrypting(false);
      setIsAuthGatewayOpen(true);
      setIsFirstLaunch(false);
    }, 350);
  };

  useEffect(() => {
    const handleLogout = () => {
      setIsDecrypting(true);
      setTimeout(() => {
        setIsDecrypting(false);
        setIsAuthGatewayOpen(true);
        setIsFirstLaunch(false);
      }, 350);
    };

    const handleLogin = (user: any) => {
      if (user) {
        setIsAuthGatewayOpen(false);
        setIsFirstLaunch(false);
        setIsDecrypting(false);
      }
    };

    const handleStateChanged = (state: any) => {
      if (!state?.currentUser) {
        setIsAuthGatewayOpen(true);
      }
    };

    if (!multiAccountVault.getCurrentUser()) {
      setIsAuthGatewayOpen(true);
    }

    multiAccountVault.on('auth:logout', handleLogout);
    multiAccountVault.on('auth:login', handleLogin);
    multiAccountVault.on('auth:stateChanged', handleStateChanged);

    sessionIntrospector.start();
    const unsub = sessionIntrospector.subscribe((res) => {
      if (res.revoked || (!res.active && !multiAccountVault.getCurrentUser())) {
        setIsAuthGatewayOpen(true);
        setIsFirstLaunch(false);
      }
    });
    return () => {
      multiAccountVault.off('auth:logout', handleLogout);
      multiAccountVault.off('auth:login', handleLogin);
      multiAccountVault.off('auth:stateChanged', handleStateChanged);
      unsub();
      sessionIntrospector.stop();
    };
  }, []);

  const [inspectingTaskId, setInspectingTaskId] = useState<string | null>(null);
  const [inspectingDealId, setInspectingDealId] = useState<string | null>(null);

  useEffect(() => {
    const handleOpenEntity = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;
      if (detail.entityType === 'task') {
        setInspectingTaskId(detail.entityId);
      } else if (detail.entityType === 'deal' || detail.entityType === 'lead') {
        setInspectingDealId(detail.entityId);
      } else if (detail.entityType === 'user') {
        setSelectedDrawerProfileId(detail.entityId);
      }
    };

    window.addEventListener('open-entity', handleOpenEntity);
    return () => window.removeEventListener('open-entity', handleOpenEntity);
  }, []);
  const [isGenesisWizardOverride, setIsGenesisWizardOverride] = useState(false);
  const [onboardingModalMode, setOnboardingModalMode] = useState<'gateway' | 'wizard'>('gateway');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);

  // Listen for IPC request to open Settings modal from main process
  useEffect(() => {
    if ((window as any).electronAPI?.receive) {
      const sub = (window as any).electronAPI.receive('open-settings-modal', () => {
        setIsSettingsOpen(true);
      });
      return () => {
        sub.unsubscribe();
      };
    }
  }, []);
  const [settingsTab, setSettingsTab] = useState<'identity' | 'members' | 'topology' | 'compute' | 'vault' | 'modules' | 'security' | 'commands' | 'observability'>('identity');
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [selectedPeer, setSelectedPeer] = useState<string | undefined>(undefined);
  const [selectedDrawerProfileId, setSelectedDrawerProfileId] = useState<string | null>(null);

  const [currentTheme, setCurrentTheme] = useState<ThemePreset>(() => getInitialTheme());

  const [activeJurisdiction, setActiveJurisdiction] = useState<string>(() => {
    return localStorage.getItem('mesh_active_jurisdiction') || 'GLOBAL';
  });

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // ── Commerce & Storefront State ───────────────────────────────────────────
  const [commerceCart, setCommerceCart] = useState<GuestCart>(() => ({
    cartId: `cart_${Date.now()}`,
    workspaceId: 'ws-default',
    salesChannelId: 'web-storefront',
    locationId: 'loc-main',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    items: [],
    subtotalMinor: 0,
    estimatedTaxMinor: 0,
    discountTotalMinor: 0,
    grandTotalMinor: 0,
    currency: 'USD',
  }));
  const [isCommerceCheckoutOpen, setIsCommerceCheckoutOpen] = useState(false);

  const handleCommerceAddItem = (item: GuestCartItem) => {
    setCommerceCart((prev) => {
      const existing = prev.items.find((i) => i.variantId === item.variantId);
      const nextItems = existing
        ? prev.items.map((i) =>
            i.variantId === item.variantId
              ? { ...i, quantity: i.quantity + item.quantity, totalMinor: (i.quantity + item.quantity) * i.unitPriceMinor }
              : i
          )
        : [...prev.items, item];

      const subtotalMinor = nextItems.reduce((sum, i) => sum + i.totalMinor, 0);
      const estimatedTaxMinor = Math.round((subtotalMinor * 825) / 10000);
      const grandTotalMinor = subtotalMinor + estimatedTaxMinor;

      return {
        ...prev,
        items: nextItems,
        subtotalMinor,
        estimatedTaxMinor,
        grandTotalMinor,
        updatedAt: Date.now(),
      };
    });
  };

  const handleCommerceUpdateQuantity = (variantId: string, delta: number) => {
    setCommerceCart((prev) => {
      const nextItems = prev.items
        .map((i) => {
          if (i.variantId === variantId) {
            const nextQty = i.quantity + delta;
            if (nextQty <= 0) return null;
            return { ...i, quantity: nextQty, totalMinor: nextQty * i.unitPriceMinor };
          }
          return i;
        })
        .filter(Boolean) as GuestCartItem[];

      const subtotalMinor = nextItems.reduce((sum, i) => sum + i.totalMinor, 0);
      const estimatedTaxMinor = Math.round((subtotalMinor * 825) / 10000);
      const grandTotalMinor = subtotalMinor + estimatedTaxMinor;

      return {
        ...prev,
        items: nextItems,
        subtotalMinor,
        estimatedTaxMinor,
        grandTotalMinor,
        updatedAt: Date.now(),
      };
    });
  };

  const handleCommerceResetCart = () => {
    setCommerceCart({
      cartId: `cart_${Date.now()}`,
      workspaceId: 'ws-default',
      salesChannelId: 'web-storefront',
      locationId: 'loc-main',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      items: [],
      subtotalMinor: 0,
      estimatedTaxMinor: 0,
      discountTotalMinor: 0,
      grandTotalMinor: 0,
      currency: 'USD',
    });
  };

  /**
   * Strict media defaults:
   * - All media flags default to false.
   * - No broadcasts are registered until the user explicitly enables them.
   * - The directory starts empty and reflects only real, user-activated resources.
   */
  const [activeMedia, setActiveMedia] = useState({
    video: false,
    audio: false,
    screen: false,
    broadcast: false,
    whiteboard: false,
    actionPlanner: false,
    agents: false,
  });

  // Determine the currently active optional module ID for the slot renderer
  const activeModuleId = activeMedia.actionPlanner
    ? 'mod-action-planner'
    : activeMedia.whiteboard
    ? 'mod-whiteboard'
    : activeMedia.agents
    ? 'mod-mesh-chat'
    : undefined;

  const [enabledPlugins, setEnabledPlugins] = useState<Record<string, boolean>>(() => {
    return getActiveModules();
  });

  useEffect(() => {
    const handlePluginsChange = () => {
      if (typeof window !== 'undefined') {
        setEnabledPlugins(getActiveModules());
      }
    };
    window.addEventListener('herx:modules-changed', handlePluginsChange);
    return () => window.removeEventListener('herx:modules-changed', handlePluginsChange);
  }, []);

  // Reactive cleanup: When plugins are disabled, close active panes and redirect from disabled rooms
  useEffect(() => {
    if (!isPluginIdEnabled(enabledPlugins, 'WhiteboardPlugin') && activeMedia.whiteboard) {
      setActiveMedia((prev) => ({ ...prev, whiteboard: false }));
    }
    if (!isPluginIdEnabled(enabledPlugins, 'ActionPlannerPlugin') && activeMedia.actionPlanner) {
      setActiveMedia((prev) => ({ ...prev, actionPlanner: false }));
    }
    if (!isPluginIdEnabled(enabledPlugins, 'AiAgentPlugin') && activeMedia.agents) {
      setActiveMedia((prev) => ({ ...prev, agents: false }));
    }
    if (!isChannelEnabled(currentRoom, enabledPlugins, activeJurisdiction)) {
      setCurrentRoom('general');
    }
  }, [enabledPlugins, currentRoom, activeJurisdiction, activeMedia.whiteboard, activeMedia.actionPlanner, activeMedia.agents]);

  // Dynamic system status derived from active components
  const systemStatus = activeMedia.whiteboard || activeMedia.broadcast || activeMedia.actionPlanner ? 'LIVE' : 'IDLE';

  // Toggle handlers for individual controls
  const handleToggleVideo = () => setActiveMedia((prev) => ({ ...prev, video: !prev.video }));
  const handleToggleAudio = () => setActiveMedia((prev) => ({ ...prev, audio: !prev.audio }));

  const handleToggleBroadcast = () => {
    setActiveMedia((prev) => {
      const nextBroadcast = !prev.broadcast;
      return {
        ...prev,
        broadcast: nextBroadcast,
        video: nextBroadcast ? true : prev.video,
        audio: nextBroadcast ? true : prev.audio,
      };
    });
  };

  const handleToggleWhiteboard = () => {
    if (!isPluginIdEnabled(enabledPlugins, 'WhiteboardPlugin')) return;
    setActiveMedia((prev) => {
      const nextWb = !prev.whiteboard;
      return { ...prev, whiteboard: nextWb };
    });
  };

  const handleToggleActionPlanner = () => {
    if (!isPluginIdEnabled(enabledPlugins, 'ActionPlannerPlugin')) return;
    setActiveMedia((prev) => {
      const nextAp = !prev.actionPlanner;
      return { ...prev, actionPlanner: nextAp };
    });
  };

  const handleToggleScreen = () => setActiveMedia((prev) => ({ ...prev, screen: !prev.screen }));

  // Auto-close notifications when clicking outside
  const notificationsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    if (isNotificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationsOpen]);

  const [userProfile, setUserProfile] = useState<UserProfileData>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`profile_data_${nodeId}`);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {}
      }
    }
    return {
      displayName: isOwner ? 'Sovereign Admin' : `Peer ${nodeId}`,
      hnsAlias: `hns://${nodeId}.mesh`,
      avatarIcon: isOwner ? '🛡️' : '👤',
      bio: isOwner ? 'Sovereign Mesh Box Owner & Genesis Node' : 'Sovereign Mesh Peer Member',
      securityTier: isOwner ? 'QUANTUM_RESISTANT' : 'CLASSICAL',
    };
  });

  const [peers, setPeers] = useState<Array<{ nodeId: string; addressUri: string; status?: 'online' | 'offline' }>>([]);

  const [broadcasts, setBroadcasts] = useState<Array<{ uri: string; topic: string }>>([]);
  // Active screen capture stream captured at the top-level window (not inside an iframe),
  // so getDisplayMedia() works in both browser and Electron contexts.
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const screenVideoRef = useRef<HTMLVideoElement>(null);

  // Removed useBroadcastPublisher: Micro-apps are now responsible for publishing their own broadcasts

  // Self-node bootstrap hydration guard (DATA-01C / Adj-3)
  useEffect(() => {
    if (!nodeId) return;
    const workspaceId = nodeId;
    const selfInDir = globalWorkspaceDirectory
      .getMembers(workspaceId)
      .some((m) => m.principalId === nodeId);

    if (!selfInDir) {
      globalWorkspaceDirectory.addMember({
        principalId: nodeId,
        displayName: userProfile.displayName,
        hnsName: `hns://${nodeId}.mesh`,
        role: isOwner ? 'admin' : 'member',
        status: 'active',
        workspaceIds: [workspaceId, '*'],
        canReceiveAssignments: true,
        canBeMentioned: true,
      });
    }
  }, [nodeId, isOwner, userProfile.displayName]);

  const [bootstrapState, setBootstrapState] = useState(() => globalWorkspaceBootstrapper.getState());

  useEffect(() => {
    const unsub = globalWorkspaceBootstrapper.subscribe(setBootstrapState);
    if (nodeId) {
      globalWorkspaceBootstrapper.runBootstrap(nodeId, userProfile.displayName);
    }
    return unsub;
  }, [nodeId, userProfile.displayName]);

  const scrumEngine = React.useMemo(() => {
    const bReg = new ChannelBindingRegistry();
    const matcher = new TriggerMatcher();
    const kanban = new InMemoryKanbanProvider(nodeId as TenantId);
    const scrum = new ScrumMasterWorkflow(kanban);

    bReg.bindAgent({
      bindingId: 'binding-scrum-01' as BindingId,
      channelId: 'scrum' as ChannelId,
      agentId: 'agent-scrum-master' as AgentId,
      tenantId: nodeId as TenantId,
      role: 'scrum_master',
      status: 'active',
      triggerPolicy: {
        respondWhenMentioned: true,
        respondToCommands: ['/standup', '/blocker', '/sprint'],
        respondToKeywords: ['standup', 'blocked', 'help', 'sprint', 'kanban'],
      },
      knowledgePolicy: { mode: 'hybrid', wikiScopes: ['scrum-guide'], ragScopes: ['current-sprint'] },
      capabilityPolicy: { allowedCapabilities: ['read_sprint', 'create_task_draft'], requiresApprovalFor: ['create_task'] },
      createdBy: nodeId as EntityId,
      updatedBy: nodeId as EntityId,
      version: 1,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });

    const cRouter = new ChannelAgentRouter(bReg, matcher, scrum);
    const propSvc = new ProposalService();
    const logger = new AuditLogger();
    const propExec = new ProposalExecutor(bReg, propSvc, kanban, logger);

    return { cRouter, scrum, propSvc, propExec };
  }, [nodeId]);

  const [scrumProposals, setScrumProposals] = useState<KanbanProposal[]>([]);

  const refreshScrumProposals = () => {
    setScrumProposals(scrumEngine.propSvc.listPendingProposals('scrum' as ChannelId, nodeId as TenantId));
  };

  useEffect(() => {
    refreshScrumProposals();
  }, [currentRoom]);

  // Pre-mount the CRM plugin store so React adapter is initialized immediately
  useEffect(() => {
    if (isPluginIdEnabled(enabledPlugins, 'CrmPlugin')) {
      crmPlugin.mountRoom('crm');
    }
  }, [enabledPlugins]);

  const handleApproveScrumProposal = async (req: ExecuteProposalRequest) => {
    await scrumEngine.propExec.executeProposal(req);
    const proposal = scrumProposals.find((p) => p.proposalId === req.proposalId) as any;
    if (proposal) {
      const targetId = proposal.targetTaskId || proposal.taskId || proposal.payload?.taskId;
      const targetStatus = proposal.payload?.status || proposal.payload?.targetState || proposal.targetState || 'in_progress';
      if (targetId) {
        updateKanbanTaskStatus(targetId, targetStatus, 'scrum');
      }
    }
    refreshScrumProposals();
  };

  const handleRejectScrumProposal = (proposalId: string) => {
    scrumEngine.propSvc.updateProposalStatus(proposalId as any, 'rejected');
    refreshScrumProposals();
  };

  const handleSubmitScrumStandup = async (standup: Omit<StandupResponse, 'submittedAt'>) => {
    scrumEngine.scrum.recordStandup('scrum' as ChannelId, {
      ...standup,
      submittedAt: Date.now(),
    });
    if (standup.blockers && standup.blockers.length > 0) {
      await scrumEngine.cRouter.dispatchChannelMessage({
        tenantId: nodeId as TenantId,
        channelId: 'scrum' as ChannelId,
        senderId: nodeId as EntityId,
        messageText: `/blocker ${standup.blockers.join(', ')}`,
      });
    }
    refreshScrumProposals();
  };

  useEffect(() => {
    applyThemeTokens(currentTheme);
  }, [currentTheme]);

  // Auto-close sidebar when going to mobile
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const loadDirectory = async () => {
      const pList = await directory.listPeers();
      const bList = await directory.listBroadcasts();
      
      setPeers((prev) => {
        const next = prev.map(p => ({ ...p, status: 'offline' as 'online' | 'offline' }));
        pList.forEach((activeP) => {
          const id = activeP.displayName || activeP.nodeId;
          const addressUri = activeP.hnsHandle || `hns://${activeP.nodeId}.mesh`;
          const existing = next.find(p => p.nodeId === id || p.nodeId.includes(activeP.nodeId) || activeP.nodeId.includes(p.nodeId));
          if (existing) {
            existing.status = activeP.status === 'stale' ? 'offline' : 'online';
            existing.nodeId = id; // update to precise ID if matched fuzzily
            existing.addressUri = addressUri;
          } else {
            next.push({ nodeId: id, addressUri, status: activeP.status === 'stale' ? 'offline' : 'online' });
          }
        });
        return next;
      });
      setBroadcasts(bList.map((b) => ({ uri: b.uri, topic: b.topic })));
    };
    loadDirectory();
    
    const unsubscribe = directory.onDirectoryChange(() => {
      loadDirectory();
    });

    const subs: Array<{ unsubscribe: () => void }> = [];

    if ((window as any).electronAPI?.receive) {
      subs.push((window as any).electronAPI.receive('open-onboarding-wizard', () => {
        setIsGenesisWizardOverride(true);
        setOnboardingModalMode('gateway');
        setIsOnboardingOpen(true);
      }));
      subs.push((window as any).electronAPI.receive('open-factory-reset', () => {
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch {}
        setIsGenesisWizardOverride(true);
        setOnboardingModalMode('wizard');
        setIsOnboardingOpen(true);
      }));
      subs.push((window as any).electronAPI.receive('open-settings-modal', () => setIsSettingsOpen(true)));
      subs.push((window as any).electronAPI.receive('open-notifications-drawer', () => setIsNotificationsOpen(true)));
    }

    // Signal main process that renderer DOM is ready for queued deep links and interactions
    if ((window as any).electronAPI?.send) {
      (window as any).electronAPI.send('app-client-ready');
    }

    return () => {
      unsubscribe();
      subs.forEach((s) => s?.unsubscribe?.());
    };
  }, [directory]);

  // Room-scoped Whiteboard Broadcast background listener
  useEffect(() => {
    const hasWbBroadcast = broadcasts.some((b) => b.topic?.includes(`🎨 Whiteboard Canvas (#${currentRoom})`));
    if (hasWbBroadcast) {
      broadcast.publish(`hts://mesh/whiteboard/${currentRoom}`, {
        v: 1,
        type: 'WHITEBOARD_REQUEST_STATE',
        fromNodeId: nodeId,
        room: currentRoom,
        requestId: `bcast-autojoin-${Date.now()}`,
      });
    }
  }, [broadcasts, currentRoom, broadcast, nodeId]);

  // Room-scoped Action Planner Broadcast background listener
  useEffect(() => {
    const hasApBroadcast = broadcasts.some((b) => b.topic?.includes(`📋 Action Planner Engine (#${currentRoom})`));
    if (hasApBroadcast) {
      broadcast.publish(`hts://mesh/action_planner/${currentRoom}`, {
        type: 'PLANNER_REQUEST_STATE',
        room: currentRoom,
        fromNodeId: nodeId,
      });
    }
  }, [broadcasts, currentRoom, broadcast, nodeId]);

  const handleToggleMedia = async (key: 'video' | 'audio' | 'screen' | 'broadcast' | 'whiteboard' | 'actionPlanner' | 'agents') => {
    if (key === 'screen') {
      if (activeMedia.screen && screenStream) {
        // Stop tracks and unregister from directory
        screenStream.getTracks().forEach(t => t.stop());
        setScreenStream(null);
        broadcast.publish(`hts://mesh/screencast/${currentRoom}`, {
          kind: 'SCREENCAST_STATE', isSharing: false, fps: 0, timestamp: Date.now(),
        });
        const bList = await directory.listBroadcasts();
        for (const b of bList) {
          if (b.publisherNodeId === directory.getLocalNodeId() && b.uri.includes('screenShare')) {
            directory.unregisterBroadcast(b.uri);
          }
        }
        setActiveMedia(prev => ({ ...prev, screen: false }));
        return;
      }
      try {
        let stream: MediaStream;
        const electronApi = (window as any).electronAPI;
        if (electronApi?.getDesktopSources) {
          try {
            const sources: any[] = await electronApi.getDesktopSources();
            if (sources && sources.length > 0) {
              stream = await (navigator.mediaDevices as any).getUserMedia({
                audio: false,
                video: {
                  mandatory: {
                    chromeMediaSource: 'desktop',
                    chromeMediaSourceId: sources[0].id,
                    minWidth: 1280,
                    maxWidth: 1920,
                    minHeight: 720,
                    maxHeight: 1080,
                  },
                },
              });
            } else {
              stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
            }
          } catch {
            stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
          }
        } else {
          stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        }

        stream.getVideoTracks()[0].addEventListener('ended', async () => {
          setScreenStream(null);
          setActiveMedia(prev => ({ ...prev, screen: false }));
          broadcast.publish(`hts://mesh/screencast/${currentRoom}`, {
            kind: 'SCREENCAST_STATE', isSharing: false, fps: 0, timestamp: Date.now(),
          });
          const bList = await directory.listBroadcasts();
          for (const b of bList) {
            if (b.publisherNodeId === directory.getLocalNodeId() && b.uri.includes('screenShare')) {
              directory.unregisterBroadcast(b.uri);
            }
          }
        });
        setScreenStream(stream);
        setActiveMedia(prev => ({ ...prev, screen: true }));
        // Register in directory so it appears in LIVE BROADCASTS sidebar
        await directory.registerBroadcast({
          uri: `hts://mesh/broadcast/${directory.getLocalNodeId()}-screenShare`,
          pluginId: 'broadcast',
          topic: `💻 Screen Share (#${currentRoom})`,
          publisherNodeId: directory.getLocalNodeId(),
          lastActive: Date.now(),
        });
        // Notify peers
        broadcast.publish(`hts://mesh/screencast/${currentRoom}`, {
          kind: 'SCREENCAST_STATE', isSharing: true, fps: 30, timestamp: Date.now(),
        });
      } catch (e: any) {
        if (e?.name !== 'NotAllowedError' && e?.name !== 'AbortError') {
          console.error('[AppShell] Screen share capture failed:', e);
        }
        // User cancelled — don't toggle
      }
      return;
    }
    if (key === 'actionPlanner' || key === 'whiteboard' || key === 'agents') {
      setActiveMedia((prev) => {
        const isTargetActive = prev[key];
        return {
          ...prev,
          actionPlanner: key === 'actionPlanner' ? !isTargetActive : false,
          whiteboard: key === 'whiteboard' ? !isTargetActive : false,
          agents: key === 'agents' ? !isTargetActive : false,
        };
      });
      return;
    }
    setActiveMedia((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Wire stream to the video element whenever it changes
  useEffect(() => {
    if (screenVideoRef.current) {
      screenVideoRef.current.srcObject = screenStream;
    }
  }, [screenStream]);

  // Presenter WebRTC Relay & Periodic Heartbeat for active screen capture
  useEffect(() => {
    if (!screenStream || !activeMedia.screen) return;

    const channelUri = `hts://mesh/screencast/${currentRoom}`;
    const peerConnections = new Map<string, { pc: RTCPeerConnection; pendingIce: RTCIceCandidateInit[] }>();

    const publishState = () => {
      broadcast.publish(channelUri, {
        kind: 'SCREENCAST_STATE',
        isSharing: true,
        fps: 30,
        presenterId: nodeId,
        timestamp: Date.now(),
      });
    };

    publishState();
    const interval = setInterval(publishState, 2000);

    const unsubscribe = broadcast.subscribe(channelUri, async (envelope: any) => {
      const payload = envelope.payload as any;
      if (!payload || envelope.senderId === nodeId) return;

      const viewerId = envelope.senderId;

      if (payload.kind === 'SCREENCAST_REQUEST_STATE') {
        publishState();

        // Always close any stale connection for this viewerId so late joiners,
        // channel switches, and reconnects get a fresh WebRTC OFFER.
        if (peerConnections.has(viewerId)) {
          try { peerConnections.get(viewerId)?.pc.close(); } catch {}
          peerConnections.delete(viewerId);
        }

        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
          ],
        });
        const entry = { pc, pendingIce: [] as RTCIceCandidateInit[] };
        peerConnections.set(viewerId, entry);

        screenStream.getTracks().forEach((track) => pc.addTrack(track, screenStream));

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            broadcast.publish(channelUri, {
              kind: 'WEBRTC_SIGNAL',
              targetId: viewerId,
              signal: { type: 'ICE_CANDIDATE', candidate: event.candidate.toJSON(), fromNodeId: nodeId },
            });
          }
        };

        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          broadcast.publish(channelUri, {
            kind: 'WEBRTC_SIGNAL',
            targetId: viewerId,
            signal: {
              type: 'OFFER',
              sdp: { type: offer.type, sdp: offer.sdp },
              fromNodeId: nodeId,
            },
          });
        } catch (err) {
          console.error('[AppShell WebRTC] Offer error:', err);
        }
      } else if (payload.kind === 'WEBRTC_SIGNAL') {
        const signal = payload.signal;
        if (payload.targetId !== nodeId && signal?.targetId !== nodeId) return;
        const entry = peerConnections.get(signal.fromNodeId || viewerId);
        if (entry) {
          const { pc, pendingIce } = entry;
          if (signal.type === 'ANSWER' && signal.sdp?.sdp) {
            try {
              const answerDesc = new RTCSessionDescription({
                type: (signal.sdp.type || 'answer') as RTCSdpType,
                sdp: signal.sdp.sdp,
              });
              await pc.setRemoteDescription(answerDesc);
              for (const cand of pendingIce) {
                await pc.addIceCandidate(new RTCIceCandidate(cand)).catch(() => {});
              }
              pendingIce.length = 0;
            } catch (err) {
              console.error('[AppShell WebRTC] Set remote description error:', err);
            }
          } else if (signal.type === 'ICE_CANDIDATE') {
            if (pc.remoteDescription && pc.remoteDescription.type) {
              try { await pc.addIceCandidate(new RTCIceCandidate(signal.candidate)); } catch {}
            } else {
              pendingIce.push(signal.candidate);
            }
          }
        }
      }
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
      peerConnections.forEach(({ pc }) => pc.close());
      peerConnections.clear();
    };
  }, [screenStream, activeMedia.screen, currentRoom, broadcast, nodeId]);

  // Whiteboard Host Broadcast & ACL Control State
  const [wbBroadcastActive, setWbBroadcastActive] = useState(false);
  const [wbCollaborators, setWbCollaborators] = useState<Set<string>>(new Set());

  // Action Planner P2P Broadcast State
  const [apBroadcastActive, setApBroadcastActive] = useState(false);

  const handleToggleActionPlannerBroadcast = async () => {
    const broadcastUri = `hts://mesh/broadcast/${nodeId}-actionPlanner`;

    if (apBroadcastActive) {
      const bList = await directory.listBroadcasts();
      for (const b of bList) {
        if (b.publisherNodeId === nodeId && b.uri.includes('actionPlanner')) {
          directory.unregisterBroadcast(b.uri);
        }
      }
      setApBroadcastActive(false);
    } else {
      setApBroadcastActive(true);
      await directory.registerBroadcast({
        uri: broadcastUri,
        pluginId: 'broadcast',
        topic: `📋 Action Planner Engine (#${currentRoom})`,
        publisherNodeId: nodeId,
        lastActive: Date.now(),
      });
      broadcast.publish(`hts://mesh/action_planner/${currentRoom}`, {
        type: 'PLANNER_REQUEST_STATE',
        room: currentRoom,
        fromNodeId: nodeId,
      });
    }
  };

  const handleToggleWhiteboardBroadcast = async () => {
    const channelUri = `hts://mesh/whiteboard/${currentRoom}`;
    const broadcastUri = `hts://mesh/broadcast/${nodeId}-whiteboard`;

    if (wbBroadcastActive) {
      broadcast.publish(channelUri, {
        v: 1,
        type: 'WHITEBOARD_TEARDOWN',
        hostNodeId: nodeId,
        room: currentRoom,
        reason: 'Host stopped broadcast',
      });
      const bList = await directory.listBroadcasts();
      for (const b of bList) {
        if (b.publisherNodeId === nodeId && b.uri.includes('whiteboard')) {
          directory.unregisterBroadcast(b.uri);
        }
      }
      setWbBroadcastActive(false);
      setWbCollaborators(new Set());
    } else {
      setWbBroadcastActive(true);
      await directory.registerBroadcast({
        uri: broadcastUri,
        pluginId: 'broadcast',
        topic: `🎨 Whiteboard Canvas (#${currentRoom})`,
        publisherNodeId: nodeId,
        lastActive: Date.now(),
      });
      broadcast.publish(channelUri, {
        v: 1,
        type: 'WHITEBOARD_SNAPSHOT',
        snapshot: canonicalSnapshotRef.current,
        hostNodeId: nodeId,
        collaborators: Array.from(wbCollaborators),
        room: currentRoom,
        requestId: `bcast-init-${Date.now()}`,
      });
    }
  };

  // Host Whiteboard ACL & State Synchronization Engine
  const canonicalSnapshotRef = useRef<any>(null);

  useEffect(() => {
    const channelUri = `hts://mesh/whiteboard/${currentRoom}`;

    const unsubscribe = broadcast.subscribe(channelUri, (envelope: any) => {
      const payload = envelope.payload;
      if (!payload || payload.room !== currentRoom) return;

      const senderId = envelope.senderId || payload.fromNodeId;

      if (payload.type === 'WHITEBOARD_ACTION' && payload.action?.snapshot) {
        canonicalSnapshotRef.current = payload.action.snapshot;
      }

      if (payload.type === 'WHITEBOARD_REQUEST_STATE') {
        broadcast.publish(channelUri, {
          v: 1,
          type: 'WHITEBOARD_SNAPSHOT',
          snapshot: canonicalSnapshotRef.current,
          toNodeId: senderId,
          hostNodeId: nodeId,
          collaborators: Array.from(wbCollaborators),
          room: currentRoom,
          requestId: payload.requestId,
        });
      } else if (payload.type === 'WHITEBOARD_ACTION') {
        const isCollaborator = wbCollaborators.has(senderId) || senderId === nodeId;
        if (!isCollaborator && wbBroadcastActive) {
          console.warn(`[Host ACL Rejection] Peer '${senderId}' blocked: missing COLLABORATOR role.`);
          return;
        }
        if (senderId !== nodeId && wbBroadcastActive) {
          broadcast.publish(channelUri, payload);
        }
      }
    });

    return () => unsubscribe();
  }, [wbBroadcastActive, currentRoom, broadcast, nodeId, wbCollaborators]);

  const handlePeerSelect = (peerId: string) => {
    setSelectedPeer(peerId);
    setSelectedDrawerProfileId(peerId);
    // Automatically grant COLLABORATOR role when inviting peer to active whiteboard
    if (wbBroadcastActive) {
      setWbCollaborators((prev) => new Set(prev).add(peerId));
      broadcast.publish(`hts://mesh/whiteboard/${currentRoom}`, {
        v: 1,
        type: 'WHITEBOARD_PERM_UPDATE',
        targetNodeId: peerId,
        newRole: 'COLLABORATOR',
        grantedBy: nodeId,
        room: currentRoom,
      });
    }
    setIsInviteOpen(true);
  };

  const handleSelectBroadcast = (b: any) => {
    const isVideo = b.topic?.startsWith('📹 Video Mesh Call');
    const isScreen = b.topic?.startsWith('💻 Screen Share');
    const isWhiteboard = b.topic?.startsWith('🎨 Whiteboard Canvas');
    const isActionPlanner = b.topic?.startsWith('📋 Action Planner Engine');
    if (!isVideo && !isScreen && !isWhiteboard && !isActionPlanner) return;

    const match = b.topic?.match(/\(#([^)]+)\)/);
    const room = match ? match[1] : 'general';

    const wbAllowed = isPluginIdEnabled(enabledPlugins, 'WhiteboardPlugin');
    const apAllowed = isPluginIdEnabled(enabledPlugins, 'ActionPlannerPlugin');

    if (isWhiteboard && wbAllowed) {
      setActiveMedia((prev) => ({ ...prev, whiteboard: true }));
      broadcast.publish(`hts://mesh/whiteboard/${room}`, {
        v: 1,
        type: 'WHITEBOARD_REQUEST_STATE',
        fromNodeId: nodeId,
        room,
        requestId: `req-${Date.now()}`,
      });
    }

    if (isActionPlanner && apAllowed) {
      setActiveMedia((prev) => ({ ...prev, actionPlanner: true }));
      broadcast.publish(`hts://mesh/action_planner/${room}`, {
        type: 'PLANNER_REQUEST_STATE',
        room,
        fromNodeId: nodeId,
      });
    }

    if (currentRoom === room) {
      if (isVideo && activeMedia.video) return;
      if (isScreen && activeMedia.screen) return;
    }

    setCurrentRoom(room);
    setActiveMedia((prev) => ({ 
      ...prev, 
      video: isVideo ? true : false,
      screen: isScreen ? true : false,
      whiteboard: (isWhiteboard && wbAllowed) ? true : (wbAllowed ? prev.whiteboard : false),
      actionPlanner: (isActionPlanner && apAllowed) ? true : (apAllowed ? prev.actionPlanner : false),
    }));
  };

  const handleSelectRoom = (room: string) => {
    if (room === currentRoom) return;
    setCurrentRoom(room);
    
    const hasVideoBroadcast = broadcasts.some(b => b.topic?.includes(`📹 Video Mesh Call (#${room})`));
    const hasScreenBroadcast = broadcasts.some(b => b.topic?.includes(`💻 Screen Share (#${room})`));
    const hasWbBroadcast = broadcasts.some(b => b.topic?.includes(`🎨 Whiteboard Canvas (#${room})`));
    const hasApBroadcast = broadcasts.some(b => b.topic?.includes(`📋 Action Planner Engine (#${room})`));
    const wbAllowed = isPluginIdEnabled(enabledPlugins, 'WhiteboardPlugin');
    const apAllowed = isPluginIdEnabled(enabledPlugins, 'ActionPlannerPlugin');
    
    setActiveMedia((prev) => ({
      ...prev,
      video: hasVideoBroadcast,
      screen: !hasVideoBroadcast && hasScreenBroadcast,
      whiteboard: wbAllowed ? (hasWbBroadcast || prev.whiteboard) : false,
      actionPlanner: apAllowed ? (hasApBroadcast || prev.actionPlanner) : false,
      audio: false,
    }));

    if (hasWbBroadcast && wbAllowed) {
      broadcast.publish(`hts://mesh/whiteboard/${room}`, {
        v: 1,
        type: 'WHITEBOARD_REQUEST_STATE',
        fromNodeId: nodeId,
        room,
        requestId: `room-switch-${Date.now()}`,
      });
    }
  };

  const handleResetNodeCache = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {}
    setIsSettingsOpen(false);
    setIsNotificationsOpen(false);
    setIsGenesisWizardOverride(true);
    setOnboardingModalMode('wizard');
    setIsOnboardingOpen(true);
  };

  const handleSelectTheme = (preset: ThemePreset) => {
    setCurrentTheme(preset);
    applyThemeTokens(preset);
  };

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClearNotifications = () => setNotifications([]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // On mobile the sidebar is ALWAYS rendered as an overlay when open —
  // it never takes up layout space, keeping the full width for content.
  const sidebarIsOverlay = isMobile || bp === 'sm';

  const allBroadcasts = [...broadcasts];

  const principalIdentity = useMemo(() => ({
    principalId: userProfile.displayName || nodeId,
    nodeId,
    displayName: userProfile.displayName,
    hnsAlias: userProfile.hnsAlias,
    avatarIcon: userProfile.avatarIcon || (isOwner ? '🛡️' : '👤'),
    isOwner,
  }), [userProfile, nodeId, isOwner]);

  const activeExecutionContext = useMemo<ExecutionContext>(() => {
    return {
      principalId: userProfile.displayName || nodeId,
      sessionId: `sess-${nodeId}`,
      nodeId,
      deviceKeyId: `key-${nodeId}`,
      meshId: verifiedPrincipal.meshId || 'mesh-sovereign-alpha',
      workspaceId: 'workspace-main',
      deviceOwnership: isOwner ? 'host-owned' : 'user-owned',
      role: isOwner ? 'owner' : 'member',
      capabilities: isOwner
        ? [
            'host.compute.configure',
            'host.gateway.configure',
            'host.models.install',
            'workspace.plugins.manage',
            'helper.lease.approve',
            'helper.lease.revoke',
            'workspace.action.execute',
          ]
        : ['workspace.action.execute', 'device.capacity.configure', 'helper.register.propose'],
    };
  }, [userProfile.displayName, nodeId, verifiedPrincipal.meshId, isOwner]);

  // HARD GATE: If unauthenticated (or during explicit signout), render ONLY the SecretKeyRegistrationGateway.
  // Nothing else in AppShell (no OnboardingModal, no WorkspaceWizard, no Sidebar, no Chat) will mount.
  const currentUser = multiAccountVault.getCurrentUser();
  const isFullyAuthenticated =
    Boolean(currentUser) &&
    Boolean(currentUser?.role) &&
    currentUser?.role !== 'guest' &&
    !isAuthGatewayOpen;

  if (!isFullyAuthenticated) {
    return (
      <SecretKeyRegistrationGateway
        onRegistered={() => {
          setIsAuthGatewayOpen(false);
          setIsFirstLaunch(false);
          setIsDecrypting(false);
        }}
      />
    );
  }

  if (bootstrapState.lifecycle === 'dependencies-pending' && bootstrapState.workspaceConfig && isOwner) {
    return (
      <PrincipalProvider identity={principalIdentity}>
        <div style={{ minHeight: '100vh', width: '100vw', background: '#090d16', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto' }}>
          <WorkspaceWizard
            onComplete={(_activation) => globalWorkspaceBootstrapper.completeWizard(bootstrapState.workspaceConfig)}
          />
        </div>
      </PrincipalProvider>
    );
  }

  return (
    <PrincipalProvider identity={principalIdentity}>
    <div
      id="app-shell-root"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
        color: 'var(--theme-text-primary, #f8fafc)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div data-testid="app-ready" aria-hidden="true" style={{ display: 'none' }} />
      {/* ── 0. Workspace Capability Degradation Banner ──────────────────────── */}
      <PluginDegradedBanner
        readinessMap={bootstrapState.pluginReadiness}
        onOpenSettings={(pluginId) => {
          if (pluginId === 'CommercePlugin') {
            globalWorkspaceBootstrapper.configurePluginSetup('CommercePlugin', 'configure-currency', true);
            setSettingsTab('modules');
          } else if (pluginId === 'InvoicingPlugin') {
            globalWorkspaceBootstrapper.configurePluginSetup('InvoicingPlugin', 'configure-efris', true);
            setSettingsTab('modules');
          } else if (pluginId === 'CrmPlugin') {
            globalWorkspaceBootstrapper.configurePluginSetup('CrmPlugin', 'configure-pipeline', true);
            setSettingsTab('modules');
          } else if (pluginId === 'ActionPlannerPlugin') {
            globalWorkspaceBootstrapper.configurePluginSetup('ActionPlannerPlugin', 'configure-workweek', true);
            setSettingsTab('modules');
          } else if (pluginId === 'LLMWikiPlugin') {
            globalWorkspaceBootstrapper.configurePluginSetup('LLMWikiPlugin', 'configure-sources', true);
            setSettingsTab('modules');
          } else {
            setSettingsTab('modules');
          }
          setIsSettingsOpen(true);
        }}
      />

      {/* ── 1. Global Top Header ─────────────────────────────────────────────── */}
      <Header
        currentRoom={currentRoom}
        onSelectRoom={setCurrentRoom}
        activeMedia={activeMedia}
        onToggleMedia={handleToggleMedia}
        enabledPlugins={enabledPlugins}
        peerCount={peers.length}
        userName={authContext.principal.displayName || userProfile.displayName}
        avatarIcon={userProfile.avatarIcon}
        avatarUrl={(authContext.principal as any).avatarUrl || userProfile.avatarUrl}
        onOpenSettings={() => {
          console.log('[Renderer] AppShell onOpenSettings handler invoked!');
          setIsSettingsOpen(true);
        }}
        onToggleSidebar={() => setIsSidebarOpen((v) => !v)}
        isSidebarOpen={isSidebarOpen}
        unreadNotifications={unreadCount}
        onToggleNotifications={() => setIsNotificationsOpen((v) => !v)}
        isCalendarOpen={isCalendarOpen}
        onToggleCalendar={() => setIsCalendarOpen((v) => !v)}
        breakpoint={breakpoint}
        currentTheme={currentTheme}
        onSelectTheme={(preset) => {
          setCurrentTheme(preset);
          applyThemeTokens(preset);
        }}
        onOpenAccountSwitcher={() => setIsAccountSwitcherOpen(true)}
        roleBadge={authContext.sessionMode.type === 'role-preview' ? `Preview: ${authContext.sessionMode.previewRole}` : (isOwner ? '👑 Owner' : authContext.roles[0])}
        onOpenCommandPalette={() => setIsCommandPaletteOpen(true)}
        onOpenHelp={() => setIsContextualHelpOpen(true)}
        onOpenDePINMarket={() => setIsDePINMarketOpen(true)}
        onOpenFleetHealth={() => setIsFleetHealthOpen(true)}
        onlineNodeCount={peers.length + 1}
        totalNodeCount={5}
        onOpenSuperadminDashboard={() => setIsSuperadminDashboardOpen(true)}
        isAdmin={authContext.roles.includes('sovereign-owner') || authContext.roles.includes('node-admin')}
      />

      {/* ── 1.1 Role Preview Sticky Notification Banner ──────────────────────── */}
      {authContext.sessionMode.type === 'role-preview' && (
        <div
          id="global-role-preview-banner"
          style={{
            height: '32px',
            backgroundColor: '#f59e0b',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            fontSize: '12px',
            fontWeight: 700,
            zIndex: 70,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <span>👁️ Role Preview Mode: Simulating '{authContext.sessionMode.previewRole}' (Kernel is Read-Only)</span>
          <button
            onClick={() => globalIdentityService.exitRolePreview()}
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              border: '1px solid #000',
              backgroundColor: '#fff',
              color: '#000',
              fontWeight: 700,
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            Exit Preview
          </button>
        </div>
      )}

      {/* ── 2. Main Workspace ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* Notification popover — position:absolute inside this relative container */}
        <NotificationPopover
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          notifications={notifications}
          onMarkAllAsRead={handleMarkAllAsRead}
          onClearNotifications={handleClearNotifications}
        />

        {/* Sidebar: overlay on mobile/tablet, inline on desktop */}
        {isSidebarOpen && (
          <Sidebar
            currentRoom={currentRoom}
            onSelectRoom={handleSelectRoom}
            peers={peers}
            broadcasts={allBroadcasts}
            isCollapsed={!sidebarIsOverlay && isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
            enabledPlugins={enabledPlugins}
            activeJurisdiction={activeJurisdiction}
            isOverlay={sidebarIsOverlay}
            onClose={() => setIsSidebarOpen(false)}
            onSelectBroadcast={handleSelectBroadcast}
            onSelectPeer={handlePeerSelect}
            onOpenChannelModal={() => setIsChannelModalOpen(true)}
            onOpenVoiceOverlay={() => setIsVoiceOverlayOpen(true)}
            onToggleNotifications={() => setIsNotificationsOpen((v) => !v)}
            unreadNotifications={unreadCount}
          />
        )}

        {/* Center: Whiteboard + Main App */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', minWidth: 0 }}>
          {/* Embedded Whiteboard, Action Planner, or Agent Control Center Top Pane */}
          {activeMedia.agents && isPluginIdEnabled(enabledPlugins, 'AiAgentPlugin') ? (
            <>
              <AgentDirectoryPane
                currentRoom={currentRoom}
                nodeId={nodeId}
                tenantId={nodeId}
                height={topPaneHeight}
              />
              {!isPaneExpanded && <ResizeHandle currentHeight={topPaneHeight} onResize={setTopPaneHeight} />}
            </>
          ) : activeMedia.actionPlanner && isPluginIdEnabled(enabledPlugins, 'ActionPlannerPlugin') ? (
            <>
              <ModuleLoader
                moduleId="org.herx.kanban"
                fallback={<div className="p-8 text-center text-slate-400">Loading Action Planner...</div>}
                currentRoom={currentRoom}
                broadcast={broadcast}
                nodeId={nodeId}
                height={topPaneHeight}
                isExpanded={isPaneExpanded}
                onToggleExpand={() => setIsPaneExpanded((v) => !v)}
                isBroadcastActive={apBroadcastActive}
                onToggleBroadcast={handleToggleActionPlannerBroadcast}
              />
              {!isPaneExpanded && <ResizeHandle currentHeight={topPaneHeight} onResize={setTopPaneHeight} />}
            </>
          ) : null}

          {/* Module / Plugin Split Pane */}
          <SovereignModuleSlotRenderer slot="top:split" />

          {activeMedia.whiteboard && isPluginIdEnabled(enabledPlugins, 'WhiteboardPlugin') ? (() => {
            const currentWbRole = !wbBroadcastActive ? 'HOST' : (wbCollaborators.has(nodeId) ? 'COLLABORATOR' : 'VIEWER');
            return (
              <>
                <ModuleLoader
                  moduleId="org.herx.whiteboard"
                  fallback={<div className="p-8 text-center text-slate-400">Loading Whiteboard...</div>}
                  currentRoom={currentRoom}
                  broadcast={broadcast}
                  nodeId={nodeId}
                  role={currentWbRole}
                  isBroadcastActive={wbBroadcastActive}
                  height={topPaneHeight}
                  onToggleBroadcast={handleToggleWhiteboardBroadcast}
                  onInviteClick={() => setIsInviteOpen(true)}
                />
                {!isPaneExpanded && <ResizeHandle currentHeight={topPaneHeight} onResize={setTopPaneHeight} />}
              </>
            );
          })() : null}

          {/* Active App Frame (Hidden when plugin pane is expanded to full view) */}
          {!isPaneExpanded && (
            <main id="main-content" style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <SovereignModuleSlotRenderer slot="main:pane" activeModuleId={activeModuleId} />
            {currentRoom === 'private' ? (
              <DockedRoomView
                roomId="private"
                currentUser={{ id: "host_admin", name: "Hercules (Host)", role: "host" }}
              />
            ) : activeMedia.video || activeMedia.audio ? (
              <AppContainer
                appId="video-micro-app"
                appUrl={`/src/mock-apps/video-app/index.html?room=${currentRoom}`}
                broadcast={broadcast}
                invite={invite as any}
                directory={directory}
              />
            ) : activeMedia.screen ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#090d16', position: 'relative' }}>
                {screenStream ? (
                  /* Presenter: local screen capture rendered directly in the parent window */
                  <>
                    <video
                      ref={screenVideoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
                    />
                    <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.7)', color: '#34d399', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }} />
                      LIVE — Sharing Screen
                    </div>
                  </>
                ) : (
                  /* Viewer: use the screencast micro-app to subscribe and show remote state */
                  <AppContainer
                    appId="screencast-micro-app"
                    appUrl={`/src/mock-apps/screencast-app/index.html?room=${currentRoom}`}
                    broadcast={broadcast}
                    invite={invite as any}
                    directory={directory}
                    unsandboxed={true}
                  />
                )}
              </div>
            ) : currentRoom === 'wall' || currentRoom === 'walls' || currentRoom === 'my-wall' || currentRoom === 'feed' || currentRoom === 'drops' ? (
              <div style={{ flex: 1, overflowY: 'auto', backgroundColor: 'var(--theme-bg-main, #0b0f19)', height: '100%', position: 'relative' }}>
                {(() => {
  const MyWallViewWidget = ExtensionRegistry.getWidget('wall:my-wall');
  return MyWallViewWidget ? <MyWallViewWidget
                  currentViewerDid={verifiedPrincipal.meshId ? `did:herx:${verifiedPrincipal.meshId}` : 'did:herx:p_admin'}
                  initialTab={currentRoom === 'feed' ? 'FOLLOWING' : currentRoom === 'drops' ? 'DROPS' : 'WALL'}
                  onBackToPortal={() => {
                    setCurrentRoom('general');
                  }}
                /> : null;
})()}
              </div>
            ) : currentRoom === 'portal' ? (
              <PortalLandingView
                onOpenDePINMarket={() => setIsDePINMarketOpen(true)}
                onOpenAccountSwitcher={() => setIsAccountSwitcherOpen(true)}
                currentUserDisplayName={userProfile.displayName || (authContext.principal as any).displayName}
                currentUserHandle={(userProfile.displayName || 'pioneer').toLowerCase().replace(/\s+/g, '') + '.herx'}
                onEnterWorkspace={() => {
                  setCurrentRoom('general');
                  setIsSidebarOpen(true);
                }}
              />
            ) : currentRoom === 'knowledge-priming' ? (
              <KnowledgePrimingDashboard />
            ) : currentRoom === 'cms-translation' ? (
              <CmsTranslationStudio />
            ) : currentRoom === 'cms' ? (
              <CmsStudioView workspaceId={nodeId} />
            ) : currentRoom === 'gallery' ? (
              <GalleryChannelView onOpenActivation={() => setIsActivationModalOpen(true)} />
            ) : (currentRoom === 'tasks' || currentRoom === 'taskflow' || currentRoom === 'planner') ? (
              <TaskFlowWorkspace workspaceId={nodeId} />
            ) : currentRoom === 'scrum' ? (
              (() => {
                const ScrumWidget = ExtensionRegistry.getWidget('scrum:channel-view');
                return ScrumWidget ? (
                  <ScrumWidget
                    channelId="scrum"
                    nodeId={nodeId}
                    proposals={scrumProposals}
                    onApproveProposal={handleApproveScrumProposal}
                    onRejectProposal={handleRejectScrumProposal}
                    onSubmitStandup={handleSubmitScrumStandup}
                  />
                ) : null;
              })()
            ) : currentRoom === 'crm' ? (
              (() => {
                const DashboardWidget = ExtensionRegistry.getWidget('crm:dashboard') as React.ComponentType<any>;
                return DashboardWidget ? <DashboardWidget nodeId={nodeId} /> : null;
              })()
            ) : currentRoom === 'pos' ? (
              (() => {
  const PosTerminalWidget = ExtensionRegistry.getWidget('pos:terminal');
  return PosTerminalWidget ? <PosTerminalWidget
                workspaceId="ws-default"
                terminalId="POS-MAIN-01"
                locationId="LOC-MAIN"
                storeName="Sovereign Cafe POS"
                catalog={getLivePublicProducts().flatMap((p) =>
                  p.variants.map((v) => ({
                    variantId: v.variantId,
                    name: p.name,
                    sku: v.sku,
                    category: p.category,
                    priceMinor: v.basePriceMinor,
                    availableQty: 20,
                    imageUrl: p.imageUrl,
                  }))
                )}
                clerks={[
                  { clerkId: 'clerk_alice', name: 'Alice (Cashier)', role: 'cashier', pinHash: '1234', salt: 'salt', capabilities: ['pos.sell', 'pos.discount'] },
                  { clerkId: 'clerk_bob', name: 'Bob (Supervisor)', role: 'supervisor', pinHash: '9999', salt: 'salt', capabilities: ['pos.sell', 'pos.discount', 'pos.void', 'pos.refund', 'pos.cash-movement', 'pos.drawer-open', 'pos.shift-close', 'pos.override'] },
                ]}
              /> : null;
})()
            ) : currentRoom === 'kiosk' ? (
              <KioskShell
                config={{
                  terminalId: 'KIOSK-COUNTER-01',
                  locationId: 'LOC-MAIN',
                  workspaceId: 'ws-default',
                  storeName: 'Sovereign Cafe & Store',
                  currency: 'USD',
                  defaultLanguage: 'en',
                  supportedLanguages: ['en', 'es', 'zh-Hans', 'ar'],
                  lowStockThreshold: 5,
                  inactivityWarningSeconds: 45,
                  inactivityResetSeconds: 60,
                  attractorMediaUrls: [],
                  promoBanners: [],
                  supervisorPinHash: '1234',
                  supervisorPinSalt: 'salt',
                  isLockedDown: false,
                }}
                products={getLivePublicProducts()}
                onExecuteCheckout={async (cmd) => {
                  return {
                    orderId: `ord_${Date.now()}`,
                    orderNumber: `GUEST-${Math.floor(Math.random() * 9000) + 1000}`,
                    claimToken: {
                      payload: {
                        protocol: 'mesh-guest-claim/v1',
                        orderId: `ord_${Date.now()}`,
                        orderNumber: `GUEST-99`,
                        workspaceId: 'ws-default',
                        salesChannelId: 'kiosk',
                        locationId: 'loc-1',
                        guestSessionId: 'sess-1',
                        totalGrossMinor: cmd.cart.grandTotalMinor,
                        currency: 'USD',
                        issuedAt: Date.now(),
                        expiresAt: Date.now() + 86400000,
                      },
                      signature: 'sample_sig',
                    },
                    pickupQrToken: `hnd_${Date.now()}`,
                  };
                }}
                onExitKiosk={() => setCurrentRoom('general')}
              />
            ) : currentRoom === 'commerce' ? (
              <>
                <KioskCatalogView
                  products={getLivePublicProducts()}
                  cart={commerceCart}
                  onAddItem={handleCommerceAddItem}
                  onUpdateQuantity={handleCommerceUpdateQuantity}
                  onOpenCheckout={() => setIsCommerceCheckoutOpen(true)}
                  onResetSession={handleCommerceResetCart}
                />
                {isCommerceCheckoutOpen && (
                  <KioskSelfCheckoutModal
                    cart={commerceCart}
                    onExecuteCheckout={async (cmd) => {
                      return {
                        orderId: `ord_${Date.now()}`,
                        orderNumber: `STORE-${Math.floor(Math.random() * 9000) + 1000}`,
                        claimToken: {
                          payload: {
                            protocol: 'mesh-guest-claim/v1',
                            orderId: `ord_${Date.now()}`,
                            orderNumber: `STORE-1`,
                            workspaceId: 'ws-default',
                            salesChannelId: 'storefront',
                            locationId: 'loc-1',
                            guestSessionId: 'sess-1',
                            totalGrossMinor: cmd.cart.grandTotalMinor,
                            currency: 'USD',
                            issuedAt: Date.now(),
                            expiresAt: Date.now() + 86400000,
                          },
                          signature: 'sample_sig',
                        },
                        pickupQrToken: `hnd_${Date.now()}`,
                      };
                    }}
                    onClose={() => setIsCommerceCheckoutOpen(false)}
                    onFinishOrder={() => {
                      setIsCommerceCheckoutOpen(false);
                      handleCommerceResetCart();
                    }}
                  />
                )}
              </>
            ) : currentRoom === 'inventory' ? (
              <InventoryManagerView authContext={authContext} />
            ) : currentRoom === 'invoicing' ? (
              <ComplianceAuditDeskView
                decisions={[]}
                pendingRetryItems={[]}
                deadLetterItems={[]}
                activeJurisdiction={activeJurisdiction}
                onProcessRetry={async () => {}}
                onOpenConfigModal={() => {}}
              />
            ) : currentRoom === 'payroll' ? (
              <PayrollManagementDesk activeJurisdiction={activeJurisdiction} />
            ) : currentRoom === 'authority-item-mappings' ? (
              <EfrisItemMappingDesk
                mappings={[]}
                onSaveMapping={async () => {}}
              />
            ) : currentRoom === 'ledger' ? (
              <div
                data-testid="general-ledger-view"
                style={{
                  padding: '24px',
                  backgroundColor: 'var(--theme-bg-main, #0b0f19)',
                  height: '100%',
                  overflowY: 'auto',
                  color: 'var(--theme-text-primary, #f8fafc)',
                  fontFamily: 'monospace',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <div>
                    <h1 style={{ fontSize: '20px', fontWeight: 800, color: '#38bdf8' }}>📒 Sovereign General Ledger</h1>
                    <p style={{ fontSize: '12px', color: '#94a3b8' }}>Cryptographic Double-Entry Financial Journal & Hash Chaining</p>
                  </div>
                  <span style={{ fontSize: '11px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
                    Ledger State: Reconciled
                  </span>
                </div>
                <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid #1e293b', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 120px 120px 100px', paddingBottom: '8px', borderBottom: '1px solid #334155', fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>
                    <span>TX ID</span>
                    <span>DESCRIPTION</span>
                    <span>DEBIT</span>
                    <span>CREDIT</span>
                    <span>STATUS</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 120px 120px 100px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '12px' }}>
                    <span style={{ color: '#60a5fa' }}>#tx-8812</span>
                    <span>Sovereign Node License Deposit</span>
                    <span style={{ color: '#34d399' }}>$12,500.00</span>
                    <span>—</span>
                    <span style={{ color: '#34d399' }}>Posted</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 120px 120px 100px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '12px' }}>
                    <span style={{ color: '#60a5fa' }}>#tx-8813</span>
                    <span>EFRIS Fiscal Tax Clearance (18%)</span>
                    <span>—</span>
                    <span style={{ color: '#f87171' }}>$2,250.00</span>
                    <span style={{ color: '#34d399' }}>Cleared</span>
                  </div>
                </div>
              </div>
            ) : (currentRoom === 'sales-pipeline' || currentRoom === 'crm-ops' || currentRoom === 'customer-success' || currentRoom === 'crm-knowledge') ? (
              (() => {
                const ChannelWidget = ExtensionRegistry.getWidget('crm:channel-view') as React.ComponentType<any>;
                return ChannelWidget ? <ChannelWidget channelId={currentRoom} nodeId={nodeId} /> : null;
              })()
            ) : (
              <GenericChannelView
                channelId={currentRoom}
                nodeId={nodeId}
                displayName={userProfile.displayName}
                broadcast={broadcast}
              />
            )}

            <DirectoryDrawer
              isOpen={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              directory={directory}
              onSelectPeer={handlePeerSelect}
              onSelectBroadcast={handleSelectBroadcast}
            />

            <InvitePopover
              isOpen={isInviteOpen}
              onClose={() => setIsInviteOpen(false)}
              inviteService={invite}
              targetNodeId={selectedPeer}
              onGrantCollaborator={(targetId) => handlePeerSelect(targetId)}
            />


            {/* ── 3. Global Calendar Drawer Overlay ── */}
            {isCalendarOpen && (
              <div
                id="global-calendar-drawer-overlay"
                style={{
                  position: 'fixed',
                  top: 'var(--app-header-height, 48px)',
                  right: 0,
                  bottom: 0,
                  width: isMobile ? '100%' : '520px',
                  backgroundColor: 'var(--theme-bg-main, #0b0f19)',
                  borderLeft: '1px solid var(--theme-border, #1e293b)',
                  boxShadow: '-8px 0 24px rgba(0,0,0,0.6)',
                  zIndex: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'slideInRight 0.2s ease-out',
                }}
              >
                {/* Calendar Drawer Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    background: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📅</span>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#60a5fa' }}>
                      Unified Calendar & Schedule
                    </span>
                  </div>
                  <button
                    id="btn-close-calendar-drawer"
                    onClick={() => setIsCalendarOpen(false)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '4px',
                      lineHeight: 1,
                    }}
                    title="Close (Esc)"
                  >
                    ✕
                  </button>
                </div>

                {/* Calendar Content */}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  {(() => {
                    const CalendarWidget = ExtensionRegistry.getWidget('calendar:unified-view') as React.ComponentType<any>;
                    return CalendarWidget ? (
                      <CalendarWidget
                        workspaceId={nodeId}
                        items={globalCalendarBridge.buildCalendarFeed(
                          globalWorkItemStore.getWorkItems(nodeId)
                        )}
                      />
                    ) : null;
                  })()}
                </div>
              </div>
            )}
          </main>
          )}
        </div>
      </div>

      {/* ── 3. Bottom Footer ─────────────────────────────────────────────────── */}
      <NodeMetricsFooter
        nodeId={nodeId}
        directory={directory}
        onOpenSettings={() => setIsSettingsOpen(true)}
        unreadNotifications={unreadCount}
        onToggleNotifications={() => setIsNotificationsOpen((v) => !v)}
        isMobile={isMobile}
      />

      {/*
        ─── PORTAL-LEVEL MODALS ──────────────────────────────────────────────────
        Must live at root level of AppShell, outside any overflow:hidden container.
        position:fixed is relative to the viewport only when no ancestor has
        overflow:hidden or transform — both of which exist in the workspace div.
      */}
      <OnboardingModal
        isOpen={isOnboardingOpen}
        onClose={() => {
          setIsOnboardingOpen(false);
          setIsGenesisWizardOverride(false);
          setOnboardingModalMode('gateway');
        }}
        onSaveProfile={(updated) => {
          setUserProfile(updated);
          const curr = multiAccountVault.getCurrentUser();
          if (curr) {
            multiAccountVault.saveUser({
              ...curr,
              displayName: updated.displayName,
              role: curr.role || (isOwner ? 'admin' : 'member'),
            });
          }
          if (typeof window !== 'undefined') {
            if (!isOwner) {
              localStorage.setItem(`peer_registered_${nodeId}`, 'true');
            }
            localStorage.setItem('sovereign_onboarding_completed', 'true');
            localStorage.setItem(`profile_configured_${nodeId}`, 'true');
            localStorage.setItem(`profile_data_${nodeId}`, JSON.stringify(updated));
          }
          directory.recordHeartbeat({
            nodeId,
            displayName: updated.displayName,
            hnsHandle: updated.hnsAlias,
            lastSeen: Date.now(),
            status: 'online',
            links: ['broadcast_channel'],
          });
        }}
        initialProfile={userProfile}
        isGenesisWizard={isElectron && (isOwner || isGenesisWizardOverride)}
        initialModalMode={onboardingModalMode}
        nodeId={nodeId}
        meshSuffix={verifiedPrincipal.meshId?.replace(/^mesh-(sovereign-)?/, '') || 'mesh'}
        onCryptographicReset={handleResetNodeCache}
        onCompleteGenesis={(config) => {
          console.log('[AppShell] Genesis configured successfully:', config);
          const ownerId = config.owner?.ownerId || nodeId;
          
          setUserProfile((prevProfile) => {
            const ownerName = config.hns?.owner?.name || prevProfile.displayName;
            const updatedProfile: UserProfileData = {
              ...prevProfile,
              displayName: ownerName,
              hnsAlias: `hns://${config.hns?.owner?.name}`,
              securityTier: 'QUANTUM_RESISTANT',
            };
            
            if (typeof window !== 'undefined') {
              localStorage.setItem('sovereign_owner_id', ownerId);
              if (config.hns?.owner?.name) {
                localStorage.setItem('sovereign_owner_alias', config.hns.owner.name);
              }
              localStorage.setItem('sovereign_genesis_configured', 'true');
              localStorage.setItem('sovereign_genesis_completed', 'true');
              localStorage.setItem('sovereign_onboarding_completed', 'true');
              localStorage.setItem(`profile_configured_${nodeId}`, 'true');
              localStorage.setItem(`profile_data_${nodeId}`, JSON.stringify(updatedProfile));
              localStorage.setItem(`profile_data_${ownerId}`, JSON.stringify(updatedProfile));
            }
            directory.recordHeartbeat({
              nodeId: ownerId,
              displayName: updatedProfile.displayName,
              hnsHandle: updatedProfile.hnsAlias,
              lastSeen: Date.now(),
              status: 'online',
              links: ['broadcast_channel'],
            });
            return updatedProfile;
          });
        }}
      />

      {isSettingsOpen && (
        <UserSettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          nodeId={nodeId}
          directory={directory}
          profile={userProfile}
          onUpdateProfile={(updated) => {
            setUserProfile(updated);
            if (typeof window !== 'undefined') {
              localStorage.setItem(`profile_data_${nodeId}`, JSON.stringify(updated));
            }
            directory.recordHeartbeat({
              nodeId,
              displayName: updated.displayName,
              hnsHandle: updated.hnsAlias,
              lastSeen: Date.now(),
              status: 'online',
              links: ['broadcast_channel'],
            });
          }}
          onResetNodeCache={handleResetNodeCache}
          currentTheme={currentTheme}
          onSelectTheme={handleSelectTheme}
          onOpenGenesisSetup={() => {
            setIsSettingsOpen(false);
            setIsNotificationsOpen(false);
            setIsGenesisWizardOverride(true);
            setOnboardingModalMode('wizard');
            setIsOnboardingOpen(true);
          }}
          isOwner={isOwner}
          settingsAccess={settingsAccess}
          verifiedPrincipal={verifiedPrincipal}
          initialTab={settingsTab}
          executionContext={activeExecutionContext}
        />
      )}

      {/* User Profile Slide-Over Drawer */}
      {selectedDrawerProfileId && (
        <UserProfileDrawer
          isOpen={!!selectedDrawerProfileId}
          onClose={() => setSelectedDrawerProfileId(null)}
          viewerId={nodeId}
          viewerTier={isOwner ? 'admins-only' : 'workspace-members'}
          profile={{
            principalId: selectedDrawerProfileId,
            displayName: selectedDrawerProfileId === nodeId ? userProfile.displayName : `Peer ${selectedDrawerProfileId.substring(0, 8)}`,
            bio: selectedDrawerProfileId === nodeId ? userProfile.bio : 'Sovereign mesh workspace member.',
            selfRatedSkills: [
              { categoryId: 'tech', selfRating: 8, updatedAt: new Date().toISOString() },
              { categoryId: 'finance', selfRating: 6, updatedAt: new Date().toISOString() },
            ],
            interests: ['mesh-networking', 'decentralized-identity', 'cryptography'],
            trustStatus: 'verified',
            profileCompleteness: 85,
            lastUpdatedAt: new Date().toISOString(),
            revision: 1,
          }}
          expertiseScores={[
            {
              categoryId: 'tech',
              taxonomyVersion: '1.0.0',
              policyVersion: '1.0.0',
              level: 'advanced',
              confidenceDescription: 'high',
              evidenceCount: 42,
            },
            {
              categoryId: 'finance',
              taxonomyVersion: '1.0.0',
              policyVersion: '1.0.0',
              level: 'established',
              confidenceDescription: 'medium',
              evidenceCount: 18,
            },
          ]}
          activeBadges={[
            {
              badgeId: 'badge-demo-1',
              badgeInstanceKey: 'demo-all-star',
              subjectId: selectedDrawerProfileId,
              badgeType: 'profile-all-star',
              isAutoAward: true,
              status: 'active',
              visibility: 'public-mesh',
              issuerId: 'system',
              criteriaVersion: '1.0.0',
              policyVersion: '1.0.0',
              evidenceRefs: [],
              taxonomyVersion: '1.0.0',
              nominatedAt: new Date().toISOString(),
              issuedAt: new Date().toISOString(),
            },
          ]}
          onEndorseSkill={(categoryId) => {
            alert(`Endorsed skill: ${categoryId}`);
          }}
          onReact={(type) => {
            alert(`Reacted: ${type}`);
          }}
        />
      )}

      {/* Sovereign DePIN Double-Auction Marketplace Modal */}
      {isDePINMarketOpen && (
        <DePINMarketplaceModal
          isOpen={isDePINMarketOpen}
          onClose={() => setIsDePINMarketOpen(false)}
        />
      )}

      {/* Mobile Device Simulator — dev/staging only, gated by Ctrl+Shift+M */}
      {isDevSimulatorOpen && (
        <MobileDeviceSimulator
          onClose={() => setIsDevSimulatorOpen(false)}
        />
      )}

      {/* Deep-Link Task Details Modal */}
      {inspectingTaskId && (
        (() => {
          const TaskModalWidget = ExtensionRegistry.getWidget('kanban:task-details-modal') as React.ComponentType<any>;
          return TaskModalWidget ? (
            <TaskModalWidget
              isOpen={!!inspectingTaskId}
              onClose={() => setInspectingTaskId(null)}
              task={(() => {
                const num = inspectingTaskId.replace('task-', '');
                const tKey = `task-${num}`;
                const info =
                  SEED_TASK_DETAILS[tKey] ||
                  SEED_TASK_DETAILS[num] ||
                  getKanbanTasks('scrum').get(tKey) ||
                  getDemoKanbanTasks().find((t) => t.id === tKey || t.id.endsWith(`-${num}`));

                const rawStatus = (info as any)?.status || (num === '92' ? 'blocked' : num === '110' ? 'review' : 'in_progress');
                const validStatus = rawStatus === 'backlog' ? 'to_do' : rawStatus;

                return {
                  id: inspectingTaskId,
                  title: (info as any)?.title || `Sprint Task #${inspectingTaskId}`,
                  status: validStatus as any,
                  assignee: (info as any)?.assignee || (num === '88' ? '@bob' : '@alice'),
                  estimate: (info as any)?.estimate || (info as any)?.estimateDays ? `${(info as any).estimate || (info as any).estimateDays + ' days'}` : '2 days',
                  priority: 'high',
                  description: (info as any)?.description || 'Core engineering deliverable for Sovereign Mesh sprint milestone.',
                  blockerWikiRef: num === '92' ? 'wiki://devops/staging-db-access' : undefined,
                };
              })()}
              onStatusChange={(newStatus) => {
                updateKanbanTaskStatus(inspectingTaskId, newStatus === 'to_do' ? 'backlog' : (newStatus as any), 'scrum');
              }}
              onOpenProfile={(principalId) => {
                setInspectingTaskId(null);
                setSelectedDrawerProfileId(principalId);
              }}
              onOpenWiki={(wikiUri) => {
                window.dispatchEvent(new CustomEvent('open-entity', { detail: { entityType: 'wiki', entityId: wikiUri } }));
              }}
            />
          ) : null;
        })()
      )}

      {/* Deep-Link CRM Deal & Lead Modal */}
      {inspectingDealId && (
        (() => {
          const ModalWidget = ExtensionRegistry.getWidget('crm:entity-modal') as React.ComponentType<any>;
          return ModalWidget ? (
            <ModalWidget
              isOpen={!!inspectingDealId}
              onClose={() => setInspectingDealId(null)}
              deal={{
                id: inspectingDealId,
                title: inspectingDealId === '101' ? 'Enterprise Mesh Pilot' : inspectingDealId === '102' ? 'Sovereign Node License' : 'Global Gov Deployment',
                stage: inspectingDealId === '101' ? 'Proposal' : 'Negotiation',
                amount: inspectingDealId === '101' ? '$45,000' : '$12,500',
                company: 'Sovereign Corp',
                healthScore: 88,
              }}
              onStageChange={(newStage) => {
                alert(`Deal @deal-${inspectingDealId} advanced to ${newStage}`);
              }}
            />
          ) : null;
        })()
      )}

      <AccountSwitcherModal
        isOpen={isAccountSwitcherOpen}
        onClose={() => setIsAccountSwitcherOpen(false)}
        authContext={authContext}
        onOpenGenesisSetup={() => {
          setIsSettingsOpen(false);
          setIsGenesisWizardOverride(true);
        }}
        onOpenSuperadminDashboard={() => setIsSuperadminDashboardOpen(true)}
      />

      {/* Sovereign Mesh Superadmin Control Plane Dashboard */}
      {isSuperadminDashboardOpen && (
        <SuperadminDashboard onClose={() => setIsSuperadminDashboardOpen(false)} />
      )}

      <StepUpMfaModal />

      {/* Screen Reader Live Announcer for WCAG 2.2 AA */}
      <ScreenReaderLiveAnnouncer />

      {/* Universal Command & Help Palette */}
      <CommandPaletteModal
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        activeRoute={currentRoom}
        enabledPlugins={enabledPlugins}
      />

      {/* Contextual In-Situ Help Drawer */}
      <ContextualHelpDrawer
        isOpen={isContextualHelpOpen}
        onClose={() => setIsContextualHelpOpen(false)}
        viewContext={{
          routeId: currentRoom,
          pluginId: currentRoom === 'scrum' ? 'scrum-master' : currentRoom === 'crm' ? 'crm-pipeline' : 'workspace-core',
        }}
      />

      {/* Channel & Community Hub Modal */}
      <ChannelManagementModal
        isOpen={isChannelModalOpen}
        onClose={() => setIsChannelModalOpen(false)}
        currentUserId={nodeId}
        onChannelCreated={(chanId) => setCurrentRoom(chanId)}
      />

      {/* Node License & Tier Activation Modal */}
      <ActivationModal
        isOpen={isActivationModalOpen}
        onClose={() => setIsActivationModalOpen(false)}
        serviceUrl="https://activate.herx.org"
      />

      {/* Instant Visual Cipher Transition Overlay on Logout */}
      {isDecrypting && (
        <DecryptRevealTransition durationMs={350} />
      )}

      {/* Zero-Trust Idle Inactivity Lock Screen */}
      <IdleLockScreen />




      {/* Generative UI Dashboard Canvas Modal */}
      {isGenUICanvasOpen && (
        <div
          data-testid="genui-canvas-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 95,
            background: 'rgba(10, 15, 29, 0.88)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            overflowY: 'auto',
          }}
        >
          <GenerativeDashboardCanvas onClose={() => setIsGenUICanvasOpen(false)} />
        </div>
      )}

      {/* Voice AI Waveform Overlay Modal */}
      {isVoiceOverlayOpen && (
        <div
          data-testid="voice-overlay-modal"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 96,
            background: 'rgba(10, 15, 29, 0.88)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <VoiceWaveformOverlay onClose={() => setIsVoiceOverlayOpen(false)} />
        </div>
      )}

      {/* AI System Architect Review Modal */}
      {isArchitectModalOpen && (
        <div
          data-testid="architect-review-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 97,
            background: 'rgba(10, 15, 29, 0.88)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            overflowY: 'auto',
          }}
        >
          <SystemArchitectReviewModal onClose={() => setIsArchitectModalOpen(false)} />
        </div>
      )}

      {/* User Management & Ambassador Governance Modal */}
      {isUserManagementOpen && (
        <div
          data-testid="user-management-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 98,
            background: 'rgba(10, 15, 29, 0.88)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            overflowY: 'auto',
          }}
        >
          <UserManagementConsole
            adminUserId="admin_1"
            onClose={() => setIsUserManagementOpen(false)}
          />
        </div>
      )}

      {/* Language & Translation Manager Modal */}
      <LanguageManagerModal
        isOpen={isLanguageManagerOpen}
        onClose={() => setIsLanguageManagerOpen(false)}
      />

      {/* Autonomous Agents Hub Modal */}
      <GStackAgentsHubModal
        isOpen={isAgentsHubOpen}
        onClose={() => setIsAgentsHubOpen(false)}
      />

      {/* Dynamic Sovereign Module Floating Modal / Drawers */}
      <SovereignModuleSlotRenderer slot="floating" />
      
      <KnockingNotificationToast knocks={knocks} onAdmit={handleAdmit} onDeny={handleDeny} />
    </div>
    </PrincipalProvider>
  );
};

